import { useNavigate } from "react-router";
import { Button, Card, Divider, Popover, Tooltip } from "antd";
import {
  CalendarOutlined,
  TeamOutlined,
  UserOutlined,
  LineChartOutlined,
  BarChartOutlined,
  BellOutlined,
} from "@ant-design/icons";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Text from "antd/es/typography/Text";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isMasked, toAmount, sumAmounts, safeCalc } from "../../../utils/maskedValue";
import { getCurrentUserRole, parseRole, Role } from "../../../config/rbac";

// Edit is only available to top roles
const canShowProjectEdit = () =>
  [Role.ADMIN, Role.PMO, Role.ACCOUNT_MANAGER].includes(
    parseRole(getCurrentUserRole()),
  );
const statusStyles = {
  Active: { color: "#059669", wash: "18,183,106", ctaBg: "#12B76A", ctaBorder: "#0E9F5D" },
  Inactive: { color: "#DC2626", wash: "225,29,72", ctaBg: "#E11D48", ctaBorder: "#C21C42" },
  "On Hold": { color: "#fabb02", wash: "245,158,11", ctaBg: "#F59E0B", ctaBorder: "#D97706" },
  Completed: { color: "#2563EB", wash: "37,99,235", ctaBg: "#2563EB", ctaBorder: "#1D4ED8" },
};

const ragConfig = {
  1: { color: "#2563EB", label: "Not Rated" },
  2: { color: "#ff0000", label: "At Risk" },
  3: { color: "#fabb02", label: "Needs Attention" },
  4: { color: "#00b050", label: "On Track" },
};

const hexToRgb = (hex) => {
  const normalized = hex.replace(/^#/, "");
  const shorthand = normalized.length === 3;
  const full = shorthand
    ? normalized
      .split("")
      .map((char) => char + char)
      .join("")
    : normalized;

  const parsed = full.match(/.{2}/g);
  if (!parsed) return null;

  const [r, g, b] = parsed.map((part) => Number.parseInt(part, 16));
  if ([r, g, b].some((value) => Number.isNaN(value))) return null;

  return { r, g, b };
};

const getRagWash = (ragStatus) => {
  const key = Number(ragStatus);
  const color = ragConfig[key]?.color;
  const rgb = color ? hexToRgb(color) : null;
  return rgb ? `${rgb.r},${rgb.g},${rgb.b}` : "";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";

  if (dateStr.includes("-")) {
    const parts = dateStr.split("-");

    if (parts[0]?.length === 4) {
      const [year, month, day] = parts;
      return `${month}/${day}/${String(year).slice(-2)}`;
    }

    const [day, month, year] = parts;
    return `${month}/${day}/${String(year).slice(-2)}`;
  }

  if (dateStr.includes("/")) {
    const [day, month, year] = dateStr.split("/");
    return `${month}/${day}/${String(year).slice(-2)}`;
  }

  return dateStr;
};

const normalizeYear = (year) => {
  const numericYear = Number(year);

  // Convert 2-digit years to 2000-based years
  // 26 -> 2026
  if (numericYear >= 0 && numericYear <= 99) {
    return 2000 + numericYear;
  }

  return numericYear;
};

const createValidDate = (year, month, day) => {
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const numericDay = Number(day);

  if (
    !Number.isInteger(numericYear) ||
    !Number.isInteger(numericMonth) ||
    !Number.isInteger(numericDay)
  ) {
    return null;
  }

  if (numericMonth < 1 || numericMonth > 12) {
    return null;
  }

  if (numericDay < 1 || numericDay > 31) {
    return null;
  }

  const date = new Date(
    numericYear,
    numericMonth - 1,
    numericDay
  );

  // Prevent JavaScript from normalizing invalid dates.
  // Example: 02/31/2026 should NOT become March 3.
  if (
    date.getFullYear() !== numericYear ||
    date.getMonth() !== numericMonth - 1 ||
    date.getDate() !== numericDay
  ) {
    return null;
  }

  return date;
};

const parseDateValue = (dateValue) => {
  if (
    dateValue === null ||
    dateValue === undefined ||
    typeof dateValue !== "string"
  ) {
    return null;
  }

  const normalized = dateValue.trim();

  if (!normalized) {
    return null;
  }

  /*
   * ---------------------------------------------------------
   * Hyphen formats
   *
   * MM-DD-YYYY
   * MM-DD-YY
   * ---------------------------------------------------------
   */
  if (normalized.includes("-")) {
    const parts = normalized.split("-");

    if (parts.length !== 3) {
      return null;
    }

    const numericParts = parts.map(Number);

    if (numericParts.some(Number.isNaN)) {
      return null;
    }

    // MM-DD-YYYY / MM-DD-YY
    const [month, day, rawYear] = numericParts;

    const year = normalizeYear(rawYear);

    return createValidDate(year, month, day);
  }

  /*
   * ---------------------------------------------------------
   * Slash formats
   *
   * MM/DD/YYYY
   * MM/DD/YY
   * ---------------------------------------------------------
   */
  if (normalized.includes("/")) {
    const parts = normalized.split("/");

    if (parts.length !== 3) {
      return null;
    }

    const numericParts = parts.map(Number);

    if (numericParts.some(Number.isNaN)) {
      return null;
    }

    const [month, day, rawYear] = numericParts;

    const year = normalizeYear(rawYear);

    return createValidDate(year, month, day);
  }

  /*
   * ---------------------------------------------------------
   * Fallback for standard date strings
   * ---------------------------------------------------------
   */
  const parsedDate = new Date(normalized);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const isOnOrBeforeCurrentDate = (dateValue) => {
  const date = parseDateValue(dateValue);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date <= today;
};

const normaliseStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const getInvoiceAmount = (invoice) =>
  toAmount(
    invoice?.grossInvoiceAmount ??
    invoice?.grossAmount ??
    invoice?.amount,
  );

const getMilestoneAmount = (milestone) =>
  toAmount(
    milestone?.amount ??
    milestone?.paymentAmount,
  );

const getInvoiceStatus = (invoice) =>
  String(
    invoice?.approvalStatusInFieldGlass ??
    invoice?.invoicingStatus ??
    invoice?.status ??
    "",
  );

const isInvoicedStatus = (status) =>
  normaliseStatus(status) === "invoiced";

const isPaidStatus = (status) =>
  normaliseStatus(status) === "paid";

const getPercentMeta = (
  value,
  isPlanned = false,
  warningThreshold,
) => {
  const hasValue =
    value !== null && value !== undefined && String(value).trim() !== "";

  if (!hasValue) {
    return {
      hasValue: false,
      value: 0,
      display: "N/A",
      color: "#262626",
    };
  }

  const numericValue = Number(value);
  let color = "#262626";

  if (
    numericValue < 0 ||
    (warningThreshold !== undefined && numericValue < warningThreshold)
  ) {
    color = "#ff0000";
  }

  return {
    hasValue: true,
    value: numericValue,
    display: `${numericValue}%`,
    color,
  };
};

const StatusIndicator = ({
  color,
  size = 14,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={{ flexShrink: 0, display: "block" }}
  >
    <circle cx="12" cy="12" r="10" fill={color} opacity="0.22" />
    <circle cx="12" cy="12" r="6" fill={color} />
  </svg>
);

const metricCard = (
  title,
  actual,
  planned,
  icon,
  accentColor,
) => {
  const displayPlanned = planned?.display || "N/A";

  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: 120,
        background: "#ffffff",
        border: "1px solid #F2F4F7",
        borderRadius: 10,
        padding: "6px 6px",
        display: "flex",
        alignItems: "flex-start",
        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
        transition: "background 0.15s ease",
      }}
    >
      <div
        style={{
          width: 25,
          height: 25,
          borderRadius: "50%",
          background: "transparent",
          border: `1.5px solid ${accentColor}`,
          display: "grid",
          placeItems: "center",
          color: accentColor,
          flexShrink: 0,
          marginRight: 3,
          fontSize: 15,
        }}
      >
        {icon}
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", minHeight: 28 }}>
          <Text
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 800,
              color: "#3c3c3e",
              lineHeight: "1.2",
              letterSpacing: "-0.1px",
              whiteSpace: "nowrap",
              paddingLeft:4
            }}
          >
            {title}
          </Text>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            fontSize: 10,
            lineHeight: 1.2,
            paddingLeft:4
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            <Text style={{ fontSize: 11, color: "#667085", fontWeight: 500 }}>
              Actual
            </Text>
            <Text style={{ fontSize: 11, fontWeight: 750, color: actual?.color || "#3c3c3e" }}>
              {actual?.display || "N/A"}
            </Text>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
            <Text style={{ fontSize: 11, color: "#667085", fontWeight: 500 }}>
              Planned
            </Text>
            <Text
              style={{ fontSize: 11, fontWeight: 750, color: planned?.color || "#3c3c3e" }}
            >
              {displayPlanned}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};
const statCol = (
  label,
  value,
  dotColor,
  valueColor = "#3c3c3e",
) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "#8A94A6",
          letterSpacing: 0.1,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Text>
    </div>
    <Text
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: valueColor,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Text>
  </div>
);

export function ProjectCard({ project }) {
  const navigate = useNavigate();

  const sowsArray = Array.isArray(project.sows) ? project.sows : [];
  const invoicesArray = Array.isArray(project.invoices) ? project.invoices : [];

  const allMilestones = sowsArray.flatMap((s) =>
    Array.isArray(s?.milestones) ? s.milestones : [],
  );

  const isCompletedMilestone = (status) =>
    ["paid", "completed", "done", "closed"].includes(normaliseStatus(status));

  const isInProgressMilestone = (status) =>
    ["in progress", "in-progress", "ongoing", "active", "started"].includes(
      normaliseStatus(status),
    );

  const totalMilestones = allMilestones.length;

  const completedMilestones = allMilestones.filter((milestone) =>
    isCompletedMilestone(milestone.status),
  ).length;

  const inProgressMilestones = allMilestones.filter(
    (milestone) =>
      !isCompletedMilestone(milestone.status) &&
      isInProgressMilestone(milestone.status),
  ).length;

  const overdueMilestones = allMilestones.filter((milestone) => {
    if (isCompletedMilestone(milestone.status)) return false;

    const milestoneEndDate =
      milestone.endDate || milestone.completionDate || milestone.dueDate;

    return isOnOrBeforeCurrentDate(milestoneEndDate);
  }).length;

  const completedPercent = totalMilestones
    ? Math.round((completedMilestones / totalMilestones) * 100)
    : 0;

  /* Total Invoiced */
  const totalInvoiced = sumAmounts(
    invoicesArray
      .filter((invoice) => isInvoicedStatus(getInvoiceStatus(invoice)))
      .map(getInvoiceAmount),
  );

  const plannedGpmMeta = getPercentMeta(project.plannedGpmPercentage, true);
  const actualGpmMeta = getPercentMeta(project.actualGpmPercentage);

  const plannedGpm = {
    ...plannedGpmMeta,
    color:
      plannedGpmMeta.hasValue && plannedGpmMeta.value < 20
        ? "#ff0000"
        : "#3c3c3e",
  };

  const actualGpm = {
    ...actualGpmMeta,
    color: (() => {
      // Case 1: Planned not present and Actual < 20
      if (!plannedGpmMeta.hasValue) {
        return actualGpmMeta.hasValue && actualGpmMeta.value < 20
          ? "#ff0000"
          : "#3c3c3e";
      }

      // Case 2: Planned present and Actual < Planned
      if (
        actualGpmMeta.hasValue &&
        actualGpmMeta.value < plannedGpmMeta.value
      ) {
        return "#ff0000";
      }

      return "#3c3c3e";
    })(),
  };
  const plannedEbitdaMeta = getPercentMeta(project.plannedEbitdaPercentage, true);
const actualEbitdaMeta = getPercentMeta(project.actualEbitdaPercentage);

const plannedEbitda = {
  ...plannedEbitdaMeta,
  color:
    plannedEbitdaMeta.hasValue && plannedEbitdaMeta.value < 20
      ? "#ff0000"
      : "#3c3c3e",
};

const actualEbitda = {
  ...actualEbitdaMeta,
  color: (() => {
    if (!plannedEbitdaMeta.hasValue) {
      return actualEbitdaMeta.hasValue && actualEbitdaMeta.value < 20
        ? "#ff0000"
        : "#3c3c3e";
    }

    return actualEbitdaMeta.hasValue &&
      actualEbitdaMeta.value < plannedEbitdaMeta.value
      ? "#ff0000"
      : "#3c3c3e";
  })(),
};

  const paidMilestoneAmount = sumAmounts(
    allMilestones
      .filter((milestone) => isPaidStatus(milestone.status))
      .map(getMilestoneAmount),
  );

  const invoiceMismatch = safeCalc(
    [paidMilestoneAmount, totalInvoiced],
    (paid, inv) => Math.abs(paid - inv),
  );

  const totalMilestoneValueTillCurrentDate = sumAmounts(
    allMilestones
      .filter((milestone) => {
        const milestoneEndDate =
          milestone.endDate || milestone.completionDate || milestone.dueDate;
        return isOnOrBeforeCurrentDate(milestoneEndDate);
      })
      .map(getMilestoneAmount),
  );

  const pendingInvoiceAmount = safeCalc(
    [totalMilestoneValueTillCurrentDate, totalInvoiced],
    (total, inv) => Math.abs(total - inv),
  );

  const formatCurrency = (value) => {
    if (isMasked(value)) return value.trim();
    const numericValue = Number(value || 0);
    return `$${Number.isFinite(numericValue) ? numericValue.toLocaleString() : "0"}`;
  };



  const greyColor = "#8A94A6";
  const greyWash = "138,148,166";

  const status = statusStyles[project.status] || statusStyles.Active;
  const normalizedStatus = normaliseStatus(project.status);
  const isInactive = normalizedStatus === "inactive";

  const hasRagStatus =
    project.ragStatus !== undefined &&
    project.ragStatus !== null &&
    String(project.ragStatus).trim() !== "";
  const ragStatusKey = Number(project.ragStatus);
  const isNotRated = ragStatusKey === 1;

  // For new / unrated projects, keep KPI accents blue while keeping the header wash neutral.
  const isBlueWithGreyGradient = !isInactive && (!hasRagStatus || isNotRated);

  let ragColor = "#2563EB";
  if (isInactive) {
    ragColor = greyColor;
  } else if (isBlueWithGreyGradient) {
    ragColor = "#2563EB";
  } else {
    ragColor = ragConfig[ragStatusKey]?.color || "#2563EB";
  }

  let ragWash = greyWash;
  if (!isInactive && !isBlueWithGreyGradient) {
    ragWash = getRagWash(project.ragStatus);
  }

  const isOnTrackStatus =
    !isInactive &&
    !isBlueWithGreyGradient &&
    (normalizedStatus === "on track" || ragStatusKey === 4);

  const inactiveStatusColor = statusStyles.Inactive.color;
  const statusDisplayColor = isInactive ? inactiveStatusColor : status.color;

  const accentGreenColor = isOnTrackStatus ? "#00b050" : ragColor;
  const accentGreenWash = "15,123,74";
  const buttonColor = isOnTrackStatus ? accentGreenColor : ragColor;
  const buttonShadow = isOnTrackStatus
    ? `0 4px 12px rgba(${accentGreenWash}, 0.16)`
    : `0 4px 12px rgba(${getRagWash(buttonColor) || "37,99,235"}, 0.16)`;

  const onEdit = (id) => {
    navigate(`/watchtower/projects/${id}/edit`, {
      state: { from: "/" },
    });
  };

  const onReview = (id) => {
    navigate("/watchtower/review", { state: { project: { id }, tab: "leadership-attention" } });
  };

  const additionalInfoContent = (
    <div style={{ width: 260 }}>
      <div
        style={{
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: "1px solid #f0f0f0",
          fontSize: 14,
          fontWeight: 600,
          color: "#1677ff",
        }}
      >
        Additional Information
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text strong style={{ fontSize: 13, color: "#434343" }}>
          <TeamOutlined style={{ marginRight: 6 }} />
          Delivery Managers
        </Text>

        <div
          style={{
            marginTop: 6,
            marginLeft: 22,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {project.dm?.length ? (
            project.dm.map((dm) => <Text key={dm.userId}>• {dm.name}</Text>)
          ) : (
            <Text type="secondary">Not Assigned</Text>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text strong style={{ fontSize: 13, color: "#434343" }}>
          <UserOutlined style={{ marginRight: 6 }} />
          Project Manager
        </Text>

        <div
          style={{
            marginTop: 6,
            marginLeft: 22,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {project.pm?.length ? (
            project.pm.map((pm) => <Text key={pm.userId}>• {pm.name}</Text>)
          ) : (
            <Text type="secondary">Not Assigned</Text>
          )}
        </div>
      </div>

      <Divider style={{ margin: "8px 0" }} />

      <div>
        <Text strong style={{ fontSize: 13, color: "#434343" }}>
          <CalendarOutlined style={{ marginRight: 6 }} />
          Duration
        </Text>

        <div style={{ marginTop: 6, marginLeft: 22 }}>
          <Text>
            {formatDate(project.startDate)}
            {" → "}
            {formatDate(project.endDate)}
          </Text>
        </div>
      </div>
    </div>
  );

  return (
    <Card
      hoverable
      onClick={() => navigate("/watchtower/projects/" + project.id)}
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 16,
        border: project.redFlagged
          ? "1.5px solid rgba(255, 0, 0, 0.81)"
          : "1px solid #EEF1F5",
        background: "#ffffff",
        transition: "all 0.2s ease",
        width: "100%",
        height: "100%",
        maxWidth: 360,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        lineHeight: "1",
        margin: "0 auto 16px",
        boxSizing: "border-box",
        cursor: "pointer"
      }}
      styles={{
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        },
      }}
    >
      {/* ===== Status-tinted header wash ===== */}
      <div
        style={{
          background: `linear-gradient(180deg, rgba(${ragWash || status.wash},0.30) 30%, rgba(${ragWash || status.wash},0.22) 35%, rgba(${ragWash || status.wash},0.14) 65%, rgba(255,255,255,0) 100%)`,
          padding: "0 10px 14px 10px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 1, // Reduced gap below status row (Was: 14)
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
                gap: 8,
                marginTop: 5,
              }}
            >
              <Text
                style={{
                  color: "#2563EB",
                  fontSize: 11,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {project.projectId || "-"}
              </Text>

              <Text
                style={{
                  color: "#2563EB",
                  fontSize: 11,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {project.sowId || "-"}
              </Text>
            </div>
          </div>

          <div
            style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
              <div
                style={{
                  width: 15,
                  height: 26,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <StatusIndicator color={statusDisplayColor} size={14} />
              </div>

            <Popover content={additionalInfoContent} trigger="hover" placement="leftTop">
              <Button
                type="text"
                size="small"
                icon={<InfoOutlinedIcon style={{ fontSize: 14 }} />}
                style={{
                  width: 15,
                  height: 26,
                  borderRadius: 8,
                }}
              />
            </Popover>

            {project.leadershipStatus ? (
              <Tooltip title="Attention required">
                <Button
                  type="text"
                  size="small"
                  icon={<NotificationsNoneIcon style={{ color: "#ff0000", fontSize: 16 }} />}
                  onClick={() => onReview(project.id)}
                  style={{
                    width: 15,
                    height: 26,
                    borderRadius: 8,
                  }}
                />
              </Tooltip>
            ) : null}

            {canShowProjectEdit() && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlinedIcon style={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(project.id);
              }}
              style={{
                width: 15,
                height: 26,
                borderRadius: 8,
              }}
            />
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}> {/* Reduced inline gap (Was: 16) */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <Text
              style={{
                fontWeight: 700,
                fontSize: 14,
                lineHeight: 1.25,
                display: "block",
                color: "#3c3c3e",
                letterSpacing: -0.3,
              }}
              // ellipsis={{ tooltip: project.name }}
            >
              {project.name}
            </Text>

            <Text
              style={{
                color: "#8A94A6",
                fontSize: 12,
                display: "block",
                // Reduced margin top (Was: 4)
                fontWeight: 450,
              }}
              ellipsis={{ tooltip: project.client }}
            >
              {project.client}
            </Text>
          </div>

          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <Text
              style={{
                display: "block",
                fontSize: 11,
                color: "#3c3c3e",
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              TCV
            </Text>
            <Text
              style={{
                display: "block",
                fontSize: 14,
                lineHeight: 1.2,
                fontWeight: 800,
                color: "#3c3c3e",
                letterSpacing: -0.4,
                // Reduced margin top (Was: 3)
              }}
            >
              {(() => {
                const tcv = project.tcv ?? project.contractValue ?? project.totalContractValue;
                if (tcv === undefined || tcv === null || tcv === "") return "-";
                return formatCurrency(tcv);
              })()}
            </Text>
          </div>
        </div>
      </div>

      {/* ===== Body ===== */}
      <div style={{ padding: "0 10px 14px 10px", display: "flex", flexDirection: "column", flex: 1 }}> {/* Added bottom padding */}
        {/* Milestone completion — single fill bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <Text style={{ fontSize: 12, fontWeight: 600, color: "#667085", whiteSpace: "nowrap" }}>
            Milestones Delivered
          </Text>
          <Text style={{ fontSize: 13, fontWeight: 700, color: "#3c3c3e", whiteSpace: "nowrap" }}>
            {`${completedMilestones} / ${totalMilestones}`}
          </Text>
        </div>
        <div
          style={{
            height: 3,
            borderRadius: 999,
            overflow: "hidden",
            background: "#EEF1F5",
            marginBottom: 12, // Reduced gap under progress bar (Was: 18)
          }}
        >
          <div
            style={{
              width: completedPercent + "%",
              height: "100%",
              borderRadius: 999,
              background: ragColor,
              minWidth: completedMilestones > 0 ? 2 : 0,
              transition: "width 0.3s ease",
            }}
          />
        </div>

        {/* Stats row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10, 
            paddingBottom: 12, 
            marginBottom: 12, 
            borderBottom: "1px solid #EEF1F5",
          }}
        >
          {statCol("Invoiced", formatCurrency(totalInvoiced), null)}
          {statCol("Mismatch", formatCurrency(invoiceMismatch), null)}
          {statCol(
            "Pending Invoice",
            formatCurrency(pendingInvoiceAmount),
            null,
          )}
        </div>

        {/* Metric cards */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap"}}>
          {metricCard("GPM", actualGpm, plannedGpm, <LineChartOutlined style={{ fontSize: 15 }} />, accentGreenColor)}
          {metricCard("EBITDA", actualEbitda, plannedEbitda, <BarChartOutlined style={{ fontSize: 15 }} />, accentGreenColor)}
        </div>
      </div>
    </Card>
  );
}